import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpenIcon, 
  AcademicCapIcon, 
  ShoppingBagIcon, 
  HeartIcon,
  WrenchScrewdriverIcon,
  SparklesIcon,
  ArrowRightIcon,
  ChevronRightIcon,
  UserIcon,
  ChatBubbleBottomCenterTextIcon,
  HandThumbUpIcon
} from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/common/Logo';
import GandalfLogo from '../components/common/GandalfLogo';

export default function Dashboard() {
  const { user } = useAuth();
  const [recentActivity, setRecentActivity] = useState({
    favorites: 0,
    contentViewed: 0
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchRecentActivity = async () => {
      if (!user) return;
      
      try {
        // Get favorite count
        const { data: favorites, error: favError } = await supabase
          .from('favorites')
          .select('*')
          .eq('user_id', user.id);

        if (!favError) {
          setRecentActivity(prev => ({
            ...prev,
            favorites: favorites?.length || 0
          }));
        }
      } catch (error) {
        console.error('Error fetching activity:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecentActivity();
  }, [user]);

  const navigationCards = [
    {
      title: 'Chat with Gandalf',
      description: 'Ask questions, get coaching, and find resources instantly.',
      icon: <GandalfLogo className="w-8 h-8" />,
      path: '/chat',
      gradient: 'from-purple-600 to-indigo-600',
      stats: 'AI-powered wisdom & search',
      badge: 'NEW',
      badgeIcon: <SparklesIcon className="w-3 h-3" />
    },
    {
      title: 'Playbook Library',
      description: 'Browse our curated collection of playbooks',
      icon: <BookOpenIcon className="w-8 h-8" />,
      path: '/content',
      gradient: 'from-blue-600 to-purple-600',
      stats: 'New drops every quarter'
    },
    {
      title: 'Marketplace',
      description: 'Highly curated partner discounts for your People team.',
      icon: <ShoppingBagIcon className="w-8 h-8" />,
      path: '/marketplace',
      gradient: 'from-orange-600 to-red-600',
      stats: 'Save $1000s on your People stack'
    },
    {
      title: 'My Collection',
      description: 'Your saved favorites and bookmarked content.',
      icon: <HeartIcon className="w-8 h-8" />,
      path: '/collection',
      gradient: 'from-pink-600 to-rose-600',
      stats: `${recentActivity.favorites} items saved`
    },
    {
      title: 'Open Org Toolkit',
      description: 'Open Org Software products and services.',
      icon: <WrenchScrewdriverIcon className="w-8 h-8" />,
      path: '/toolkit',
      gradient: 'from-indigo-600 to-blue-600',
      stats: 'Community-driven tools'
    },
    {
      title: 'Academy',
      description: 'Cohorts and courses that get our seal of approval.',
      icon: <AcademicCapIcon className="w-8 h-8" />,
      path: '/academy',
      gradient: 'from-green-600 to-teal-600',
      badge: 'Coming Soon',
      badgeIcon: <SparklesIcon className="w-3 h-3" />,
      stats: 'Curated learning experiences'
    }
  ];

  const quickActions = [
    {
      title: 'Browse Latest Content',
      description: 'See what\'s new in the library',
      path: '/content',
      icon: <SparklesIcon className="w-5 h-5" />
    },
    {
      title: 'Explore Discounts',
      description: 'Find deals on popular tools',
      path: '/marketplace',
      icon: <ShoppingBagIcon className="w-5 h-5" />
    },
    {
      title: 'View My Collection',
      description: 'Check your saved items',
      path: '/collection',
      icon: <HeartIcon className="w-5 h-5" />
    },
    {
      title: 'Your Membership',
      description: 'Manage your account and subscription',
      path: '/account',
      icon: <UserIcon className="w-5 h-5" />
    },
    {
      title: 'Vote on Roadmap',
      description: 'Help shape our future features',
      path: 'https://openorg.featurebase.app/roadmap',
      icon: <HandThumbUpIcon className="w-5 h-5" />,
      external: true
    }
  ];

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800">
        <div className="absolute inset-0 bg-gray-900/50"></div>
        
        <div className="relative px-6 py-12 mx-auto max-w-7xl lg:px-8">
          <div className="text-center">
            <div className="mb-8">
              <Logo size="large" centered={true} />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Welcome back to{' '}
              <span className="bg-gradient-to-r from-primary to-primary-light bg-clip-text text-transparent">
                Open Org
              </span>
            </h1>
            <p className="max-w-2xl mx-auto mt-6 text-xl text-gray-300">
              Your hub for progressive people practices. Where would you like to explore today?
            </p>
          </div>
        </div>
      </div>

      {/* Main Navigation Cards */}
      <div className="px-6 py-12 mx-auto max-w-7xl lg:px-8">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {navigationCards.map((card, index) => {
            const isComingSoon = card.badge === 'Coming Soon';
            const Component = isComingSoon ? 'div' : Link;
            const componentProps = isComingSoon ? {} : { to: card.path };
            
            return (
              <Component
                key={card.path}
                {...componentProps}
                className={`group relative overflow-hidden rounded-2xl bg-gray-900 border border-gray-800 transition-all duration-300 ${
                  isComingSoon 
                    ? 'cursor-not-allowed opacity-75' 
                    : 'hover:border-gray-700 hover:scale-105 cursor-pointer'
                }`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${card.gradient} opacity-5 ${!isComingSoon && 'group-hover:opacity-10'} transition-opacity`}></div>
                
                <div className="relative p-8">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 rounded-xl bg-gradient-to-br ${card.gradient} text-white shadow-lg`}>
                      {card.icon}
                    </div>
                    {card.badge && (
                      <span className={`px-2 py-1 text-xs font-medium rounded-full flex items-center gap-1 ${
                        card.title === 'Chat with Gandalf' 
                          ? 'bg-gradient-to-r from-purple-500/20 to-indigo-500/20 text-purple-300 border border-purple-400/30 animate-pulse shadow-lg shadow-purple-500/20' 
                          : 'bg-primary/20 text-primary-light animate-pulse'
                      }`}>
                        <span className={card.title === 'Chat with Gandalf' ? 'animate-pulse' : ''}>
                          {card.badgeIcon}
                        </span>
                        {card.badge}
                      </span>
                    )}
                  </div>
                  
                  <h3 className={`text-xl font-semibold text-white mb-3 transition-colors ${!isComingSoon && 'group-hover:text-primary-light'}`}>
                    {card.title}
                  </h3>
                  
                  <p className="text-gray-400 mb-4 leading-relaxed">
                    {card.description}
                  </p>
                  
                  <div className="flex items-end justify-between">
                    <div className="text-sm text-gray-500">
                      {card.stats}
                    </div>
                    {!isComingSoon && (
                      <ChevronRightIcon className="w-5 h-5 text-gray-400 group-hover:text-white group-hover:translate-x-1 transition-all" />
                    )}
                  </div>
                </div>
              </Component>
            );
          })}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="px-6 pb-12 mx-auto max-w-7xl lg:px-8">
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8">
          <h2 className="text-2xl font-semibold text-white mb-6">Quick Actions</h2>
          
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {quickActions.map((action, index) => {
              const isExternal = action.external;
              const Component = isExternal ? 'a' : Link;
              const componentProps = isExternal 
                ? { href: action.path, target: '_blank', rel: 'noopener noreferrer' }
                : { to: action.path };
              
              return (
                <Component
                  key={action.path}
                  {...componentProps}
                  className="group flex items-center p-4 rounded-xl bg-gray-800/50 hover:bg-gray-800 border border-gray-700/50 hover:border-gray-600 transition-all"
                >
                  <div className="flex-shrink-0 p-2 rounded-lg bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors">
                    {action.icon}
                  </div>
                  <div className="ml-4">
                    <h3 className="font-medium text-white group-hover:text-primary-light transition-colors">
                      {action.title}
                    </h3>
                    <p className="text-sm text-gray-400">
                      {action.description}
                    </p>
                  </div>
                  <ArrowRightIcon className="w-4 h-4 text-gray-400 group-hover:text-white group-hover:translate-x-1 transition-all ml-auto" />
                </Component>
              );
            })}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      {!isLoading && (
        <div className="px-6 pb-12 mx-auto max-w-7xl lg:px-8">
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 border border-gray-800 rounded-2xl p-8">
            <h2 className="text-2xl font-semibold text-white mb-6">Your Activity</h2>
            
            <div className="grid gap-6 md:grid-cols-2">
              <div className="text-center p-6 bg-gray-800/30 rounded-xl border border-gray-700/50">
                <div className="text-3xl font-bold text-primary mb-2">
                  {recentActivity.favorites}
                </div>
                <div className="text-sm text-gray-400">Items in Collection</div>
              </div>
              
              <div className="text-center p-6 bg-gray-800/30 rounded-xl border border-gray-700/50">
                <div className="text-3xl font-bold text-green-400 mb-2">
                  ∞
                </div>
                <div className="text-sm text-gray-400">Resources Available</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 